import { useMemo, useState } from 'react';
import { ChevronRight, Layers, List, RefreshCw, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { cn } from '../../../lib/utils';
import {
  CatalogSourceBadge,
  CATALOG_SOURCE_SELECT_OPTIONS,
  isErrorEventSeverity,
  isSeverityCatalogSource,
  SEVERITY_SELECT_OPTIONS,
  SeverityBadge,
} from './badges';
import { SeverityResolutionPopover } from './SeverityResolutionPopover';
import { ErrorEventSeverity, SeverityCatalogEntry, SeverityGroup } from '../../../services/api';
import { SeverityResolution, SeverityScope } from './severityResolution';
import {
  groupCatalogRows,
  loadSeverityCatalogView,
  saveSeverityCatalogView,
  SeverityCatalogView,
} from './catalogView';

/** Sentinel für „erbt" bzw. „alle" — Radix-Select verträgt keinen leeren Wert. */
const INHERIT_VALUE = '__inherit__';
const NO_GROUP_VALUE = '__none__';
const ALL_VALUE = 'all';

/** Sentinel für die Service-Wildcard — die Regel gilt für jeden Sender. */
const ANY_SERVICE_VALUE = '__any_service__';

/** Aufbereitete Tabellenzeile — die Severity-Auflösung passiert im Tab, nicht hier. */
export interface SeverityCatalogRow {
  entry: SeverityCatalogEntry;
  /** Gruppen-Zuordnung inklusive gepufferter Änderung. */
  groupId: string | null;
  groupName: string | null;
  resolution: SeverityResolution;
  /** Identifier-Regel im aktiven Geltungsbereich — `null`, wenn dort keine liegt (erbt). */
  scopedSeverity: ErrorEventSeverity | null;
  /** Effektive Severity im globalen Geltungsbereich — Vergleichsspalte im Kunden-Scope. */
  globalSeverity: ErrorEventSeverity;
  /** Service, auf den die Regel verfeinert ist — `null` = gilt für alle Services. */
  ruleService: string | null;
  /** `true`, wenn zum Identifier mehrere Regeln existieren (Wildcard und/oder service-genau). */
  hasMultipleServiceRules: boolean;
}

export interface SeverityCatalogTableProps {
  rows: SeverityCatalogRow[];
  groups: SeverityGroup[];
  scope: SeverityScope;
  customerName?: string;
  /** Gesamtzahl aller Katalog-Einträge, für „x von y". */
  catalogTotal: number;
  /** Beschriftung des aktiven Gruppen-Filters aus der Master-Spalte. */
  groupFilterLabel: string | null;
  onClearGroupFilter: () => void;
  onGroupChange: (errorIdentifier: string, groupId: string | null) => void;
  onSeverityChange: (errorIdentifier: string, severity: ErrorEventSeverity | null) => void;
  /** `null` setzt die Regel auf die Service-Wildcard zurück. */
  onRuleServiceChange: (errorIdentifier: string, service: string | null) => void;
  /** Severity der Gruppe im aktiven Geltungsbereich — `null`, wenn dort keine Regel liegt. */
  groupSeverityOf: (groupId: string) => ErrorEventSeverity | null;
  /** Globale Severity der Gruppe — im Kunden-Geltungsbereich die geerbte Basis. */
  groupGlobalSeverityOf: (groupId: string) => ErrorEventSeverity | null;
  onGroupSeverityChange: (groupId: string, severity: ErrorEventSeverity | null) => void;
  onReload: () => void;
  reloading: boolean;
}

/**
 * Detail-Spalte des Severity-Tabs: durchsuchbarer Identifier-Katalog mit
 * Gruppen-Zuordnung, Identifier-Severity und berechneter effektiver Severity.
 *
 * Zwei Darstellungen, umschaltbar oben rechts: „Gruppen" (Voreinstellung)
 * gliedert den Katalog in aufklappbare Sektionen je Fehler-Gruppe und macht die
 * Gruppen-Severity direkt in der Kopfzeile pflegbar; „Liste" ist die flache
 * Tabelle über alle Identifier. Die Wahl wird pro Browser gemerkt.
 *
 * Such-, Herkunfts- und Severity-Filter sind bewusst lokal — sie sind reine
 * Anzeigefilter und sollen den Änderungspuffer des Tabs nicht berühren.
 *
 * TODO: Massenzuordnung mehrerer Identifier per Checkbox-Auswahl (bewusst
 * zurückgestellt, siehe Umfangsabsprache).
 */
export function SeverityCatalogTable({
  rows,
  groups,
  scope,
  customerName,
  catalogTotal,
  groupFilterLabel,
  onClearGroupFilter,
  onGroupChange,
  onSeverityChange,
  onRuleServiceChange,
  groupSeverityOf,
  groupGlobalSeverityOf,
  onGroupSeverityChange,
  onReload,
  reloading,
}: SeverityCatalogTableProps) {
  const isCustomerScope = scope === 'customer';

  const [view, setView] = useState<SeverityCatalogView>(loadSeverityCatalogView);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState(ALL_VALUE);
  const [severityFilter, setSeverityFilter] = useState(ALL_VALUE);
  const [overridesOnly, setOverridesOnly] = useState(false);
  const [openSections, setOpenSections] = useState<ReadonlySet<string>>(new Set());

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (term && !row.entry.errorIdentifier.toLowerCase().includes(term)) return false;
      if (isSeverityCatalogSource(sourceFilter) && row.entry.source !== sourceFilter) return false;
      if (isErrorEventSeverity(severityFilter) && row.resolution.severity !== severityFilter) return false;
      if (overridesOnly && row.scopedSeverity === null) return false;
      return true;
    });
  }, [rows, search, sourceFilter, severityFilter, overridesOnly]);

  const hasLocalFilter =
    search.trim().length > 0 ||
    sourceFilter !== ALL_VALUE ||
    severityFilter !== ALL_VALUE ||
    overridesOnly;

  // Leere Gruppen sind ohne Filter das Signal „hier fehlt noch die Zuordnung",
  // mit Filter nur Rauschen. Bleibt gar keine Zeile übrig, gehören sie ebenfalls
  // weg — 16 leere Kopfzeilen sind kein Ersatz für „hier ist nichts".
  const hideEmptyGroups = hasLocalFilter || visibleRows.length === 0;

  const sections = useMemo(
    () => groupCatalogRows(visibleRows, groups, hideEmptyGroups),
    [visibleRows, groups, hideEmptyGroups]
  );

  // Bei aktivem Filter bleibt nichts zugeklappt — sonst versteckt die Ansicht
  // genau die Treffer, nach denen gesucht wurde. Dasselbe gilt, wenn die
  // Master-Spalte ohnehin schon auf eine einzige Gruppe eingegrenzt hat.
  const forceExpanded = hasLocalFilter || sections.length === 1;
  const allExpanded = sections.length > 0 && sections.every((section) => openSections.has(section.key));
  /** „Ohne Gruppe" ist eine Sektion, aber keine Gruppe — sonst zählt die Kopfzeile falsch. */
  const groupSectionCount = sections.filter((section) => section.group !== null).length;

  const resetLocalFilters = () => {
    setSearch('');
    setSourceFilter(ALL_VALUE);
    setSeverityFilter(ALL_VALUE);
    setOverridesOnly(false);
  };

  const changeView = (next: SeverityCatalogView) => {
    setView(next);
    saveSeverityCatalogView(next);
  };

  const toggleSection = (key: string) => {
    setOpenSections((previous) => {
      const next = new Set(previous);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  };

  const toggleAllSections = () => {
    setOpenSections(allExpanded ? new Set() : new Set(sections.map((section) => section.key)));
  };

  const scopedColumnLabel = isCustomerScope
    ? `Für ${customerName ?? 'Kunde'}`
    : 'Severity (Identifier)';

  // Im Kunden-Geltungsbereich ist die Gruppen-Spalte nur ein Anzeige-Badge —
  // in der Gruppen-Ansicht wiederholt sie die Sektions-Kopfzeile und entfällt.
  // Global bleibt sie in beiden Ansichten, weil sie dort das einzige Mittel ist,
  // einen Identifier umzuhängen.
  const showGroupColumn = view === 'list' || !isCustomerScope;

  const columnCount =
    1 + (isCustomerScope ? 0 : 2) + (showGroupColumn ? 1 : 0) + (isCustomerScope ? 1 : 0) + 2;

  const rowProps = {
    isCustomerScope,
    scope,
    customerName,
    groups,
    showGroupColumn,
    onGroupChange,
    onSeverityChange,
    onRuleServiceChange,
  };

  const emptyMessage =
    rows.length === 0 ? 'Keine Identifier in dieser Auswahl' : 'Kein Identifier passt zu den Filtern';

  return (
    <Card>
      <CardHeader className="border-b pb-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle>{isCustomerScope ? `Identifier-Overrides für ${customerName ?? 'Kunde'}` : 'Identifier-Katalog'}</CardTitle>
            <CardDescription>
              {visibleRows.length} von {catalogTotal} Identifiern
              {view === 'groups' ? ` in ${groupSectionCount} Gruppen` : ''}
              {groupFilterLabel ? ` · gefiltert auf „${groupFilterLabel}"` : ''}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex gap-1 rounded-lg bg-gray-100 p-1" role="group" aria-label="Darstellung">
              {([
                { value: 'groups', label: 'Gruppen', Icon: Layers },
                { value: 'list', label: 'Liste', Icon: List },
              ] as const).map(({ value, label, Icon }) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={view === value}
                  onClick={() => changeView(value)}
                  className={cn(
                    'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                    view === value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              ))}
            </div>

            {isCustomerScope && (
              <Select
                value={overridesOnly ? 'overrides' : 'all'}
                onValueChange={(value) => setOverridesOnly(value === 'overrides')}
              >
                <SelectTrigger size="sm" className="w-[150px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Identifier</SelectItem>
                  <SelectItem value="overrides">Nur Overrides</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Button variant="outline" size="sm" onClick={onReload} disabled={reloading}>
              <RefreshCw className={cn('mr-2 h-3.5 w-3.5', reloading && 'animate-spin')} />
              Aktualisieren
            </Button>
          </div>
        </div>
      </CardHeader>

      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Identifier suchen …"
            className="h-8 w-[230px] pl-8 text-sm"
          />
        </div>

        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger size="sm" className="w-[150px] text-xs">
            <SelectValue placeholder="Quelle: Alle" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>Quelle: Alle</SelectItem>
            {CATALOG_SOURCE_SELECT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger size="sm" className="w-[160px] text-xs">
            <SelectValue placeholder="Severity: Alle" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>Severity: Alle</SelectItem>
            {SEVERITY_SELECT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {groupFilterLabel && (
          <Badge variant="secondary" className="gap-1.5 font-normal">
            Gruppe: {groupFilterLabel}
            <button
              type="button"
              onClick={onClearGroupFilter}
              className="text-gray-400 hover:text-gray-700"
              aria-label="Gruppen-Filter entfernen"
            >
              ×
            </button>
          </Badge>
        )}

        <div className="ml-auto flex items-center gap-1">
          {view === 'groups' && !forceExpanded && sections.length > 0 && (
            <Button variant="ghost" size="sm" onClick={toggleAllSections}>
              {allExpanded ? 'Alle zuklappen' : 'Alle aufklappen'}
            </Button>
          )}
          {hasLocalFilter && (
            <Button variant="ghost" size="sm" onClick={resetLocalFilters}>
              Zurücksetzen
            </Button>
          )}
        </div>
      </div>

      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  Identifier
                </th>
                {!isCustomerScope && (
                  <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                    Quelle
                  </th>
                )}
                {!isCustomerScope && (
                  <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                    Beobachtete Sender
                  </th>
                )}
                {showGroupColumn && (
                  <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                    Gruppe
                  </th>
                )}
                {isCustomerScope && (
                  <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                    Global effektiv
                  </th>
                )}
                <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  {scopedColumnLabel}
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  Effektiv
                </th>
              </tr>
            </thead>

            {view === 'list' ? (
              <tbody>
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={columnCount} className="py-10 text-center text-sm text-gray-500">
                      {emptyMessage}
                    </td>
                  </tr>
                )}
                {visibleRows.map((row) => (
                  <CatalogRow key={row.entry.id} row={row} {...rowProps} />
                ))}
              </tbody>
            ) : (
              <>
                {sections.length === 0 && (
                  <tbody>
                    <tr>
                      <td colSpan={columnCount} className="py-10 text-center text-sm text-gray-500">
                        {emptyMessage}
                      </td>
                    </tr>
                  </tbody>
                )}

                {sections.map((section) => {
                  const group = section.group;
                  const expanded = forceExpanded || openSections.has(section.key);
                  const scopedSeverity = group ? groupSeverityOf(group.id) : null;
                  const globalSeverity = group ? groupGlobalSeverityOf(group.id) : null;

                  return (
                    <tbody key={section.key} className="border-b border-gray-200 last:border-b-0">
                      <tr className={cn('bg-gray-50', group === null && 'bg-amber-50')}>
                        <td colSpan={columnCount} className="px-0 py-0">
                          <div
                            className={cn(
                              'flex flex-wrap items-center gap-3 border-l-[3px] px-4 py-2.5',
                              expanded ? 'border-l-gray-900' : 'border-l-transparent'
                            )}
                          >
                            <button
                              type="button"
                              onClick={() => toggleSection(section.key)}
                              aria-expanded={expanded}
                              className="flex min-w-0 flex-1 items-center gap-2 text-left"
                            >
                              <ChevronRight
                                className={cn(
                                  'h-3.5 w-3.5 flex-none text-gray-400 transition-transform',
                                  expanded && 'rotate-90'
                                )}
                              />
                              <span className="min-w-0">
                                <span className="block text-sm font-semibold text-gray-900">
                                  {group?.name ?? 'Ohne Gruppe'}
                                </span>
                                <span className="mt-0.5 block truncate text-xs text-gray-500">
                                  {group?.description ??
                                    'Noch nicht zugeordnet — landet beim Rückfallwert'}
                                </span>
                              </span>
                            </button>

                            <span className="flex flex-none items-center gap-2">
                              <Badge variant="outline" className="font-normal text-gray-500">
                                {section.rows.length} Identifier
                              </Badge>

                              {group === null ? (
                                <span className="text-xs italic text-amber-700">keine Gruppen-Severity</span>
                              ) : (
                                <>
                                  {isCustomerScope && globalSeverity && (
                                    <span className="opacity-60" title="Globale Severity">
                                      <SeverityBadge severity={globalSeverity} />
                                    </span>
                                  )}
                                  <Select
                                    value={scopedSeverity ?? INHERIT_VALUE}
                                    onValueChange={(value) =>
                                      onGroupSeverityChange(
                                        group.id,
                                        isErrorEventSeverity(value) ? value : null
                                      )
                                    }
                                  >
                                    <SelectTrigger
                                      size="sm"
                                      className={cn(
                                        'h-7 min-w-[128px] text-xs',
                                        scopedSeverity === null && 'border-dashed bg-white text-gray-400',
                                        isCustomerScope && scopedSeverity !== null && 'border-amber-300'
                                      )}
                                      aria-label={
                                        isCustomerScope
                                          ? `Override für Gruppe ${group.name}`
                                          : `Default-Severity für Gruppe ${group.name}`
                                      }
                                    >
                                      <SelectValue placeholder={isCustomerScope ? '—' : 'Keine'} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value={INHERIT_VALUE}>
                                        {isCustomerScope ? '— erbt global' : 'Keine — Rückfallwert'}
                                      </SelectItem>
                                      {SEVERITY_SELECT_OPTIONS.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                          {option.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </>
                              )}
                            </span>
                          </div>
                        </td>
                      </tr>

                      {expanded &&
                        section.rows.map((row) => (
                          <CatalogRow key={row.entry.id} row={row} indented {...rowProps} />
                        ))}

                      {expanded && section.rows.length === 0 && (
                        <tr>
                          <td colSpan={columnCount} className="px-4 py-4 pl-10 text-xs italic text-gray-400">
                            Dieser Gruppe ist noch kein Identifier zugeordnet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  );
                })}
              </>
            )}
          </table>
        </div>

        {!isCustomerScope && (
          <div className="flex flex-wrap gap-4 border-t bg-gray-50 px-4 py-3 text-xs text-gray-600">
            <span className="flex items-center gap-1.5">
              <CatalogSourceBadge source="registered" /> aus dem Code-Katalog
            </span>
            <span className="flex items-center gap-1.5">
              <CatalogSourceBadge source="observed" /> nur im Betrieb aufgetaucht
            </span>
            <span className="flex items-center gap-1.5">
              <span className="rounded border border-dashed border-gray-300 bg-gray-50 px-2 py-0.5 text-gray-400">
                leer
              </span>
              erbt von Gruppe bzw. Rückfallwert
            </span>
            <span className="flex items-center gap-1.5">
              <span className="rounded border border-dashed border-gray-300 px-2 py-0.5 text-gray-500">
                Alle Services
              </span>
              Regel gilt unabhängig vom Sender — „Nur x" verfeinert und sticht sie
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface CatalogRowProps {
  row: SeverityCatalogRow;
  isCustomerScope: boolean;
  scope: SeverityScope;
  customerName?: string;
  groups: SeverityGroup[];
  /** In der Gruppen-Ansicht des Kunden-Scopes ausgeblendet — die Sektion trägt die Gruppe. */
  showGroupColumn: boolean;
  /** Rückt die erste Zelle ein, damit die Zeile als Kind der Sektion lesbar ist. */
  indented?: boolean;
  onGroupChange: (errorIdentifier: string, groupId: string | null) => void;
  onSeverityChange: (errorIdentifier: string, severity: ErrorEventSeverity | null) => void;
  onRuleServiceChange: (errorIdentifier: string, service: string | null) => void;
}

/** Eine Katalog-Zeile — identisch in beiden Darstellungen, nur unterschiedlich eingerückt. */
function CatalogRow({
  row,
  isCustomerScope,
  scope,
  customerName,
  groups,
  showGroupColumn,
  indented = false,
  onGroupChange,
  onSeverityChange,
  onRuleServiceChange,
}: CatalogRowProps) {
  const identifier = row.entry.errorIdentifier;
  const isOverridden = isCustomerScope && row.scopedSeverity !== null;
  // Regeln gelten standardmäßig für alle Services; die Verfeinerung auf einen
  // Sender ist optional und daher nur bei gesetzter Severity überhaupt sichtbar.
  const showServiceRefinement = row.scopedSeverity !== null;
  const serviceOptions = Array.from(
    new Set([...row.entry.services, ...(row.ruleService ? [row.ruleService] : [])])
  );

  return (
    <tr
      className={cn(
        'border-b border-gray-100 last:border-b-0',
        isOverridden ? 'bg-amber-50 hover:bg-amber-100/60' : 'hover:bg-gray-50'
      )}
    >
      <td className={cn('px-4 py-2.5', indented && 'pl-10')}>
        <span className="font-mono text-sm text-gray-800">{identifier}</span>
        {row.entry.description && (
          <span className="mt-0.5 block text-xs text-gray-400">{row.entry.description}</span>
        )}
        {row.hasMultipleServiceRules && (
          <span className="mt-0.5 block text-xs text-amber-700">
            Mehrere Regeln hinterlegt — angezeigt wird die spezifischste
            {row.ruleService ? ` (für ${row.ruleService})` : ' (Alle Services)'}
          </span>
        )}
      </td>

      {!isCustomerScope && (
        <td className="px-4 py-2.5">
          <CatalogSourceBadge source={row.entry.source} />
        </td>
      )}

      {!isCustomerScope && (
        <td className="px-4 py-2.5">
          <span className="flex flex-wrap gap-1">
            {row.entry.services.length === 0 && (
              <span className="text-xs italic text-gray-400">nie beobachtet</span>
            )}
            {row.entry.services.map((service) => (
              <span
                key={service}
                className="rounded border border-gray-200 bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-600"
              >
                {service}
              </span>
            ))}
          </span>
        </td>
      )}

      {showGroupColumn && (
        <td className="px-4 py-2.5">
          {isCustomerScope ? (
            row.groupName ? (
              <Badge variant="outline" className="font-normal">
                {row.groupName}
              </Badge>
            ) : (
              <span className="text-xs italic text-gray-400">ohne Gruppe</span>
            )
          ) : (
            <Select
              value={row.groupId ?? NO_GROUP_VALUE}
              onValueChange={(value) => onGroupChange(identifier, value === NO_GROUP_VALUE ? null : value)}
            >
              <SelectTrigger
                size="sm"
                className={cn(
                  'h-7 w-[170px] text-xs',
                  row.groupId === null && 'border-dashed bg-gray-50 text-gray-400'
                )}
                aria-label={`Gruppe für ${identifier}`}
              >
                <SelectValue placeholder="Keine Gruppe" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_GROUP_VALUE}>Keine Gruppe</SelectItem>
                {groups.map((group) => (
                  <SelectItem key={group.id} value={group.id}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </td>
      )}

      {isCustomerScope && (
        <td className="px-4 py-2.5">
          <span className="opacity-60">
            <SeverityBadge severity={row.globalSeverity} />
          </span>
        </td>
      )}

      <td className="px-4 py-2.5">
        <Select
          value={row.scopedSeverity ?? INHERIT_VALUE}
          onValueChange={(value) =>
            onSeverityChange(identifier, isErrorEventSeverity(value) ? value : null)
          }
        >
          <SelectTrigger
            size="sm"
            className={cn(
              'h-7 w-[140px] text-xs',
              row.scopedSeverity === null && 'border-dashed bg-gray-50 text-gray-400',
              isOverridden && 'border-amber-300 bg-white'
            )}
            aria-label={`Severity für ${identifier}`}
          >
            <SelectValue placeholder={row.groupName ? 'Von Gruppe' : 'Rückfall'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={INHERIT_VALUE}>
              {row.groupName ? '— von Gruppe' : '— Rückfallwert'}
            </SelectItem>
            {SEVERITY_SELECT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {showServiceRefinement && (
          <div className="mt-1.5">
            <Select
              value={row.ruleService ?? ANY_SERVICE_VALUE}
              onValueChange={(value) =>
                onRuleServiceChange(identifier, value === ANY_SERVICE_VALUE ? null : value)
              }
            >
              <SelectTrigger
                size="sm"
                className={cn(
                  'h-6 w-[140px] text-[11px]',
                  row.ruleService === null && 'border-dashed text-gray-500'
                )}
                aria-label={`Geltungsbereich der Regel für ${identifier}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY_SERVICE_VALUE}>Alle Services</SelectItem>
                {serviceOptions.map((service) => (
                  <SelectItem key={service} value={service}>
                    Nur {service}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </td>

      <td className="px-4 py-2.5">
        <span className="flex items-center gap-1">
          <SeverityBadge severity={row.resolution.severity} />
          <SeverityResolutionPopover
            errorIdentifier={identifier}
            resolution={row.resolution}
            scope={scope}
            customerName={customerName}
          />
        </span>
      </td>
    </tr>
  );
}
